import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import CreateJob from "./pages/CreateJob";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Footer from "./components/Footer";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ProtectedRoute from "./components/ProtectedRoute";
import JobDetails from "./pages/JobDetails";
import UserProfile from "./pages/UserProfile";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminBlog from "./pages/AdminBlog";
import CreateBlogPost from "./pages/CreateBlogPost";
import Home from "./pages/Home";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AdminPassword from "./pages/AdminPassword";
import { ADMIN_LOGIN_PATH } from "./constants/routes";
import { Seo } from "./components/Seo";
import { appMainClasses, appShellClasses } from "./styles/componentClasses";

const AdminSeoGuard: React.FC = () => {
    const location = useLocation();

    if (!location.pathname.startsWith("/admin") || location.pathname.startsWith("/admin/blog/preview/")) {
        return null;
    }

    return (
        <Seo
            title="Admin"
            description="Private admin area for Summer Jobs in Greece."
            path={location.pathname}
            robots="noindex,nofollow"
        />
    );
};

const AppShell: React.FC = () => {
    const { isAdmin } = useAuth();

    return (
        <Router>
            <AdminSeoGuard />
            <div className={appShellClasses}>
                <Navbar />
                <main className={appMainClasses}>
                    <Routes>
                        <Route path="/" element={<Blog />} />
                        <Route path="/home" element={isAdmin ? <Home /> : <Navigate to="/blog" replace />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/login" element={<Navigate to="/blog" replace />} />
                        <Route
                            path={ADMIN_LOGIN_PATH}
                            element={isAdmin ? <Navigate to="/admin/blog" replace /> : <Login />}
                        />
                        <Route
                            path="/about"
                            element={
                                <ProtectedRoute>
                                    <About />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/contact"
                            element={
                                <ProtectedRoute>
                                    <Contact />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                <ProtectedRoute>
                                    <Register />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/job/:id"
                            element={
                                <ProtectedRoute>
                                    <JobDetails />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/createJob"
                            element={
                                <ProtectedRoute>
                                    <CreateJob />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/editJob/:id"
                            element={
                                <ProtectedRoute>
                                    <CreateJob />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/jobs"
                            element={
                                <ProtectedRoute>
                                    <UserProfile />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/blog"
                            element={
                                <ProtectedRoute>
                                    <AdminBlog />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/blog/create"
                            element={
                                <ProtectedRoute>
                                    <CreateBlogPost />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/blog/edit/:id"
                            element={
                                <ProtectedRoute>
                                    <CreateBlogPost />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/blog/preview/:id"
                            element={
                                <ProtectedRoute>
                                    <BlogPost />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/password"
                            element={
                                <ProtectedRoute>
                                    <AdminPassword />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/blog" replace />} />
                    </Routes>
                </main>
                <Footer />
            </div>
        </Router>
    );
};

const App: React.FC = () => {
    return (
        <AuthProvider>
            <AppShell />
        </AuthProvider>
    );
};

export default App;
